import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from '@phosphor-icons/react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { createProjectTableColumns } from '../../components/project-table-columns/project-table-columns';
import { ProjectFormDialog } from '../../components/project-form-dialog/project-form-dialog';
import { useDeleteProject, useGetProjects } from '../../hooks/use-projects';
import { DEFAULT_PROJECTS_ORDER } from '../../graphql/queries';
import { Project, ProjectsPagingResult } from '../../types/project.types';
import { PageHeader } from '@/components/core';

export const ProjectManagerPage = () => {
  const { t } = useTranslation();

  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_PROJECTS_ORDER,
  });
  const [formState, setFormState] = useState<{ open: boolean; project: Project | null }>({
    open: false,
    project: null,
  });
  const [deleteState, setDeleteState] = useState<{ open: boolean; project: Project | null }>({
    open: false,
    project: null,
  });

  const { data, isLoading, error } = useGetProjects({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere({}, columnWhere),
    order,
  });
  const result = (data as { Projects?: ProjectsPagingResult })?.Projects;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const { mutateAsync: deleteProject, isPending: isDeleting } = useDeleteProject();

  const handleDeleteConfirm = async () => {
    if (!deleteState.project) return;
    await deleteProject(deleteState.project.ItemId);
    setDeleteState({ open: false, project: null });
  };

  const columns = createProjectTableColumns({
    t,
    onEdit: (project) => setFormState({ open: true, project }),
    onDelete: (project) => setDeleteState({ open: true, project }),
  });

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader
        title={t('PROJECTS')}
        actions={[
          {
            label: t('CREATE_PROJECT'),
            icon: <Plus className="size-5" />,
            onClick: () => setFormState({ open: true, project: null }),
          },
        ]}
      />

      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <ProjectFormDialog
        open={formState.open}
        onOpenChange={(open) =>
          setFormState((prev) => ({ open, project: open ? prev.project : null }))
        }
        project={formState.project}
      />

      <ConfirmationModal
        open={deleteState.open}
        onOpenChange={(open) =>
          setDeleteState({ open, project: open ? deleteState.project : null })
        }
        title={t('DELETE_PROJECT')}
        description={t('ARE_YOU_SURE_WANT_DELETE')}
        confirmText={isDeleting ? t('DELETING') : t('DELETE')}
        preventAutoClose={isDeleting}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};
